import { useEffect, useRef } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseVerticalLineValuesProps {
  verticalLines: VerticalLine[];
  data: Record<string, any>;
  enabledSensors: Record<string, Record<string, boolean>>;
  updateAllVerticalLineValues: (
    getValuesAtTimestamp: (timestamp: Date | number, existingValues: Record<string, number>) => Record<string, number>
  ) => void;
}

/**
 * Updates vertical line values based on sensor data
 * Finds the closest sensor value for each vertical line timestamp
 */
export function useVerticalLineValues({
  verticalLines,
  data,
  enabledSensors,
  updateAllVerticalLineValues,
}: UseVerticalLineValuesProps): void {
  // Track the previous vertical lines to detect when new lines are added
  const prevVerticalLinesCountRef = useRef(0);

  // Effect to update vertical line values when new sensor data is loaded or vertical lines are added
  useEffect(() => {
    if (verticalLines.length === 0) {
      prevVerticalLinesCountRef.current = 0;
      return;
    }

    // Build a complete map of ALL values for ALL vertical lines from ALL sections
    const allLineValues = new Map<string, Record<string, number>>();

    verticalLines.forEach((line: VerticalLine) => {
      allLineValues.set(line.id, {});
    });

    // For each section with data, collect values for all enabled sensors
    Object.keys(data).forEach((sectionType) => {
      const sectionData = data[sectionType];
      if (!sectionData || !sectionData.data || !Array.isArray(sectionData.data)) return;

      // Get all enabled sensors for this section
      const sectionEnabledSensors = enabledSensors[sectionType];
      if (!sectionEnabledSensors) return;

      // For each enabled sensor in this section
      Object.entries(sectionEnabledSensors).forEach(([sensorName, isEnabled]) => {
        if (!isEnabled) return;

        // Find the sensor's data in the section data array by displayName
        const sensorTimeSeries = sectionData.data.find(
          (s: any) => s.displayName === sensorName || s.name === sensorName
        );

        if (
          sensorTimeSeries?.x &&
          sensorTimeSeries?.y &&
          Array.isArray(sensorTimeSeries.x) &&
          Array.isArray(sensorTimeSeries.y)
        ) {
          // Detect x-axis type from first x value
          const firstX = sensorTimeSeries.x[0];
          const xAxisType: 'date' | 'numeric' | 'string' =
            firstX instanceof Date
              ? 'date'
              : typeof firstX === 'number'
              ? 'numeric'
              : typeof firstX === 'string' && !isNaN(new Date(firstX).getTime())
              ? 'date'
              : 'string';

          // For each vertical line, find the closest value
          verticalLines.forEach((line: VerticalLine) => {
            let closestValue = null;
            let minDiff = Infinity;
            let closestDataValue: any = null;

            // Convert line.timestamp to comparable value based on x-axis type
            let lineValue: number | string;
            if (xAxisType === 'date') {
              // Date axis: convert to milliseconds
              lineValue =
                line.timestamp instanceof Date ? line.timestamp.getTime() : new Date(line.timestamp as any).getTime();
            } else if (xAxisType === 'numeric') {
              // Numeric axis: use number directly
              lineValue = typeof line.timestamp === 'number' ? line.timestamp : Number(line.timestamp);
            } else {
              // String axis: use string directly
              lineValue = String(line.timestamp);
            }

            for (let i = 0; i < sensorTimeSeries.x.length; i++) {
              let dataValue: number | string;
              let diff: number;

              if (xAxisType === 'date') {
                // Date axis: convert to milliseconds for comparison
                const dataX = sensorTimeSeries.x[i];
                dataValue = dataX instanceof Date ? dataX.getTime() : new Date(dataX).getTime();
                diff = Math.abs((dataValue as number) - (lineValue as number));
              } else if (xAxisType === 'numeric') {
                // Numeric axis: direct numeric comparison
                dataValue =
                  typeof sensorTimeSeries.x[i] === 'number' ? sensorTimeSeries.x[i] : Number(sensorTimeSeries.x[i]);
                diff = Math.abs((dataValue as number) - (lineValue as number));
              } else {
                // String axis: exact match only (diff = 0 if match, Infinity if not)
                dataValue = String(sensorTimeSeries.x[i]);
                diff = dataValue === lineValue ? 0 : Infinity;
              }

              if (diff < minDiff) {
                minDiff = diff;
                closestValue = sensorTimeSeries.y[i];
                closestDataValue = dataValue;
              }
            }

            if (closestValue !== null) {
              const lineValues = allLineValues.get(line.id);
              if (lineValues) {
                lineValues[sensorName] = closestValue;
              }
            }
          });
        }
      });
    });

    // Now update all vertical lines at once with their complete values
    if (allLineValues.size > 0) {
      updateAllVerticalLineValues((timestamp: Date | number, existingValues: Record<string, number>) => {
        // Find matching line by comparing timestamps based on their type
        const line = verticalLines.find((l: VerticalLine) => {
          // Both Date objects
          if (l.timestamp instanceof Date && timestamp instanceof Date) {
            return l.timestamp.getTime() === timestamp.getTime();
          }
          // Both numbers
          if (typeof l.timestamp === 'number' && typeof timestamp === 'number') {
            return l.timestamp === timestamp;
          }
          // Both strings or mixed types - compare as strings
          return String(l.timestamp) === String(timestamp);
        });

        if (line) {
          const newValues = allLineValues.get(line.id);
          const hasNewValues = newValues && Object.keys(newValues).length > 0;

          if (hasNewValues) {
            // MERGE new values with existing values to preserve values that weren't recalculated
            return { ...existingValues, ...newValues };
          } else {
            // Keep existing values if new calculation is empty to prevent data loss
            return existingValues;
          }
        }
        return existingValues;
      });
    }

    prevVerticalLinesCountRef.current = verticalLines.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabledSensors, verticalLines.length]);
}
